import React, { useState, useRef } from "react"
import PropTypes from "prop-types"
import { Form, Button, Card, Space, Row, Col, Spin } from "antd"
import { useTranslation } from "react-i18next"
import { CustomForm } from "ui/Form"
import { useWatch } from "antd/es/form/Form"
import { useSelector } from "react-redux"
import dayjs from "dayjs"
import ClientDetail from "./ClientDetail"
import ExperienceDetail from "./ExperienceDetail"
import PaymentMethod from "./PaymentMethod"
import PriceBreakdown from "./PriceBreakdown"
import Stepper from "./Stepper"
import styles from "./BookingForm.module.css"
import { Text, Title } from "ui/Typography"

const STEP1_BASE_FIELDS = ["experience", "date", "duration", "time"]
const STEP1_NEW_CLIENT_FIELDS = ["firstName", "lastName", "email"]
const STEP1_EXISTING_CLIENT_FIELDS = ["existing_client", "email"]

const BookingForm = ({ form, bookingUuid, bookAgain, booking, onSubmit, onCancelModal, filterExperienceIds }) => {
  const { t } = useTranslation()
  const [existingClientId, setExistingClientId] = useState(null)
  const [currentStep, setCurrentStep] = useState(1)
  const scrollContainerRef = useRef(null)
  const [summaryData, setSummaryData] = useState({})
  const mode = "guideBooking"
  const isExistingClient = useWatch("isExistingClient", form)
  const [alertMessage, setAlertMessage] = useState("")
  const isSearching = useSelector((state) => state.bookings.existing_clients_list.loading)
  const {
    experienceBookingData: { bookingSelections },
    experienceById: { experienceDetail }
  } = useSelector((state) => state.experiences)

  const handleContinue = async () => {
    const clientFields = isExistingClient ? STEP1_EXISTING_CLIENT_FIELDS : STEP1_NEW_CLIENT_FIELDS
    const accessibilityRequired = form.getFieldValue("accessibility_required")
    const fieldsToValidate = [
      ...clientFields,
      ...STEP1_BASE_FIELDS,
      ...(accessibilityRequired ? ["accessibility_notes"] : [])
    ]
    try {
      await form.validateFields(fieldsToValidate)
      if (alertMessage) return
      setSummaryData(form.getFieldsValue(true))
      setCurrentStep(2)
      scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" })
    } catch {
      // validation errors shown inline
    }
  }

  const clientDisplayName = summaryData.isExistingClient
    ? summaryData.existing_client?.label
    : [summaryData.firstName, summaryData.lastName].filter(Boolean).join(" ")
  const summaryEmail = summaryData.email
  const experienceName = summaryData.experience?.label || experienceDetail?.title

  return (
    <Spin spinning={isSearching}>
    <CustomForm layout="vertical" form={form}>
      <Stepper activeStep={currentStep} t={t} />
      <div className={styles.scrollContainer} ref={scrollContainerRef}>
        {currentStep === 1 ? (
          <>
            {/* Client Details */}
            <Card className={styles.cardContainer}>
              <Title className={styles.cardTitle} level={5}>
                {t("booking.client_detail")}
              </Title>
              <ClientDetail
                setExistingClientId={setExistingClientId}
                form={form}
                bookingId={bookingUuid}
                alertMessage={alertMessage}
                setAlertMessage={setAlertMessage}
                booking={booking}
              />
            </Card>

            {/* Experience Details */}
            <Card className={styles.cardContainer}>
              <Title className={styles.cardTitle} level={5}>
                {t("booking.experience_details")}
              </Title>
              <ExperienceDetail
                form={form}
                bookingId={bookingUuid}
                booking={booking}
                bookAgain={bookAgain}
                mode={mode}
                filterExperienceIds={filterExperienceIds}
              />
            </Card>
          </>
        ) : (
          <>
            {/* Booking Summary */}
            <Card className={styles.cardContainer}>
              <Title className={styles.cardTitle} level={5}>
                {t("booking.booking_summary", "Booking Summary")}
              </Title>
              <Row gutter={[16, 8]}>
                <Col xs={24} sm={12}>
                  <Text fw={600} type="secondary">{t("booking.labels.client_name", "Client Name")}: </Text>
                  <Text>{clientDisplayName || "—"}</Text>
                </Col>
                <Col xs={24} sm={12}>
                  <Text fw={600} type="secondary">{t("booking.labels.client_email", "Client Email")}: </Text>
                  <Text>{summaryEmail || "—"}</Text>
                </Col>
                <Col xs={24} sm={12}>
                  <Text fw={600} type="secondary">{t("booking.labels.experience")}: </Text>
                  <Text>{experienceName || "—"}</Text>
                </Col>
                <Col xs={24} sm={12}>
                  <Text fw={600} type="secondary">{t("booking.labels.booked_date")}: </Text>
                  {bookingSelections?.date ? (
                    <>
                      <Text>{dayjs(bookingSelections.date).format("MM/DD/YYYY")}</Text>
                      {bookingSelections?.timeSlot && <Text type="secondary"> · {bookingSelections.timeSlot}</Text>}
                    </>
                  ) : (
                    <Text>—</Text>
                  )}
                </Col>
              </Row>
            </Card>

            {/* Payment Method */}
            <Card className={styles.cardContainer}>
              <Title className={styles.cardTitle} level={5}>
                {t("booking.payment_method")}
              </Title>
              <PaymentMethod
                existingClientId={existingClientId}
                setExistingClientId={setExistingClientId}
                form={form}
                bookingId={bookingUuid}
                booking={booking}
                bookAgain={bookAgain}
                clientEmail={summaryData.email}
              />
            </Card>

            {/* Price Breakdown */}
            <Card className={styles.cardContainer}>
              <Title className={styles.cardTitle} level={5}>
                {t("booking.price_breakdown")}
              </Title>
              <PriceBreakdown form={form} booking={booking} />
            </Card>
          </>
        )}
      </div>

      <Form.Item className={styles.formActions}>
        <Space>
          {currentStep === 1 ? (
            <>
              <Button variant="outlined" color="primary" htmlType="button" onClick={() => onCancelModal()}>
                {t("global.cancel")}
              </Button>
              <Button type="primary" htmlType="button" onClick={handleContinue}>
                {t("booking.continue_to_payment", "Continue to Payment")}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outlined" color="primary" htmlType="button" onClick={() => setCurrentStep(1)}>
                {t("global.back", "Back")}
              </Button>
              <Button type="primary" htmlType="button" onClick={onSubmit}>
                {bookingUuid && !bookAgain ? t("global.update") : t("global.submit")}
              </Button>
            </>
          )}
        </Space>
      </Form.Item>
    </CustomForm>
    </Spin>
  )
}

BookingForm.propTypes = {
  form: PropTypes.object.isRequired,
  bookingUuid: PropTypes.string.isRequired,
  bookAgain: PropTypes.object.isRequired,
  booking: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancelModal: PropTypes.func.isRequired,
  filterExperienceIds: PropTypes.arrayOf(PropTypes.number) // Optional - for filtering by equipment
}

export default BookingForm
